// src/pages/team/TeamMembersPage.tsx

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n, useAuth } from '@react/hooks';
import { teamApi } from '@core/api';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  ErrorAlert,
  Badge,
} from '@react/components';
import {
  membershipStatusMap,
  invitationStatusMap,
  getStatusMapping,
  formatRelativeTime,
  formatDate,
  getEntityId,
} from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import { UserPicker } from '@/components/UserPicker';
import { Select } from '@/components/Select';
import { useOpenDirectConversation } from '@/features/chat/useOpenDirectConversation';
import { EntityAvatar } from '@/components/EntityAvatar';

type MembersTab = 'members' | 'invitations';

// ─── Member Card ─────────────────────────────────────────────────

function MemberCard({ member, teamId, canRemove, isRemoving, onRemove, onCancelRemove, t, locale }: {
  member: any;
  teamId: string;
  canRemove: boolean;
  isRemoving: boolean;
  onRemove: () => void;
  onCancelRemove: () => void;
  t: (key: string, params?: Record<string, string>) => string;
  locale: string;
}) {
  const auth = useAuth();
  const dm = useOpenDirectConversation();
  const memberId = getEntityId(member.membership) || member.user?.entity?.id || '';
  const profileId = member.membership?.profile?.id || member.user?.entity?.id || '';
  const displayName = member.user?.display_name || memberId || '--';
  const avatarUrl = member.user?.avatar || '';
  const statusInfo = getStatusMapping(membershipStatusMap, member.membership?.status || 'MEMBERSHIP_STATUS_ACTIVE');
  const isOwner = member.is_owner;
  const isAdmin = member.is_admin;
  const roles = member.roles?.roles || [];

  return (
    <div className="group relative rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-md transition-all p-4">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <EntityAvatar name={displayName} image={avatarUrl} profileId={profileId} className="h-12 w-12" />
          {/* Status dot */}
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-card ${
              member.membership?.status === 'MEMBERSHIP_STATUS_ACTIVE' ? 'bg-emerald-500' :
              member.membership?.status === 'MEMBERSHIP_STATUS_PENDING' ? 'bg-amber-500' :
              member.membership?.status === 'MEMBERSHIP_STATUS_SUSPENDED' ? 'bg-red-500' : 'bg-gray-400'
            }`}
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={profileId ? `/userprofiles/${profileId}` : `/teams/${teamId}/members/${memberId}`}
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors truncate"
            >
              {displayName}
            </Link>
            {isOwner && (
              <Badge variant="warning" size="sm">
                <svg className="h-3 w-3 mr-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M2 20h20l-2-12-5 5-3-7-3 7-5-5-2 12z" /></svg>
                {t('team.field.owner')}
              </Badge>
            )}
            {isAdmin && !isOwner && (
              <Badge variant="primary" size="sm">
                <svg className="h-3 w-3 mr-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                Admin
              </Badge>
            )}
            <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>
          </div>

          {/* Secondary info */}
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            {member.membership?.joined_at && (
              <span className="flex items-center gap-1">
                <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                {formatRelativeTime(member.membership.joined_at, locale)}
              </span>
            )}
          </div>

          {/* Roles */}
          {roles.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {roles.map((role: any) => (
                <span
                  key={getEntityId(role) || role.name}
                  className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: role.color ? `${role.color}15` : 'var(--muted)',
                    color: role.color || 'var(--muted-foreground)',
                  }}
                >
                  {role.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Meta column: timestamps */}
        <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0 text-xs text-muted-foreground">
          {member.membership?.joined_at && (
            <span className="flex items-center gap-1">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              {formatRelativeTime(member.membership.joined_at, locale)}
            </span>
          )}
        </div>
      </div>

      {/* Actions row */}
      {(profileId || (canRemove && !isOwner)) && (
        <div className="flex items-center justify-end mt-3 pt-3 border-t border-border/50">
          {isRemoving ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-destructive mr-2">{t('team.removeMemberConfirm')}</span>
              <button
                type="button"
                onClick={onRemove}
                className="text-xs px-2.5 py-1 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                {t('common.confirm')}
              </button>
              <button
                type="button"
                onClick={onCancelRemove}
                className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:bg-muted transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Link to={`/teams/${teamId}/members/${memberId}`} className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">{t('common.view')}</Link>
              {profileId && <Link to={`/userprofiles/${profileId}`} className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">{t('team.member.openProfile')}</Link>}
              {profileId && profileId !== auth.activeProfileId && <button type="button" disabled={dm.isOpening} onClick={() => dm.open(profileId)} className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50">{t('chat.people.message')}</button>}
              {canRemove && !isOwner && <button
                type="button"
                onClick={onRemove}
                className="text-xs px-2.5 py-1 rounded-md text-destructive/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
              >{t('common.remove')}</button>}
            </div>
          )}
          {Boolean(dm.error) && <span className="ml-2 text-xs text-destructive">{t('chat.people.openFailed')}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Invitation Card ────────────────────────────────────────────

function InvitationCard({ inv, isCancelling, onCancel, onCancelConfirm, t, locale }: {
  inv: any;
  isCancelling: boolean;
  onCancel: () => void;
  onCancelConfirm: () => void;
  t: (key: string) => string;
  locale: string;
}) {
  const statusInfo = getStatusMapping(invitationStatusMap, inv.status || 'INVITATION_STATUS_PENDING');
  const isPending = inv.status === 'INVITATION_STATUS_PENDING';

  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/30 hover:shadow-md transition-all">
      {/* User icon */}
      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">
            {inv.invitee_profile?.display_name || inv.invitee_email || '--'}
          </span>
          <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          {inv.inviter?.display_name && (
            <span>{t('team.memberInvitedBy')}: {inv.inviter.display_name}</span>
          )}
          <span>{t('common.createdAt')}: {formatDate(inv.sent_at, locale)}</span>
          {inv.expires_at && <span>{t('common.expiresAt')}: {formatDate(inv.expires_at, locale)}</span>}
        </div>
      </div>

      {/* Actions */}
      {isPending && (
        <div className="flex-shrink-0">
          {isCancelling ? (
            <div className="flex items-center gap-1">
              <button type="button" onClick={onCancelConfirm} className="text-xs px-2.5 py-1 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors">
                {t('common.confirm')}
              </button>
              <button type="button" onClick={onCancel} className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:bg-muted transition-colors">
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className="text-xs px-2.5 py-1 rounded-md text-destructive/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              {t('team.cancelInvitation')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export const TeamMembersPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const store = useTeam();
  const { t, locale } = useI18n();

  const initialTab = searchParams.get('tab') === 'invitations' ? 'invitations' : 'members';
  const [activeTab, setActiveTab] = useState<MembersTab>(initialTab);
  const [members, setMembers] = useState<any[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [showAddMember, setShowAddMember] = useState(false);
  const [addProfileId, setAddProfileId] = useState('');
  const [adding, setAdding] = useState(false);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteProfileId, setInviteProfileId] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);

  const [removingId, setRemovingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [canInvite, setCanInvite] = useState(false);
  const [canRemove, setCanRemove] = useState(false);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setCanInvite(false);
    setCanRemove(false);
    teamApi.GetTeamDetails(id).then((res) => {
      if (!alive) return;
      const allowed = Boolean(res.permissions?.can_invite_members);
      setCanInvite(allowed);
      if (!allowed) setActiveTab('members');
      setCanRemove(Boolean(res.permissions?.can_remove_members));
    }).catch(() => { if (alive) setActiveTab('members'); });
    return () => { alive = false; };
  }, [id]);

  const loadMembers = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamMembers(id);
      const _list = (res as any)?.users?.users ?? (res as any)?.members ?? [];
      setMembers(Array.isArray(_list) ? _list : []);
    } catch { /* store handles */ }
  }, [id, store]);

  const loadInvitations = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamInvitations(id);
      setInvitations(res?.invitations?.invitations ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadMembers();
    if (canInvite) loadInvitations();
  }, [id, canInvite, loadMembers, loadInvitations]);

  // Client-side filtering
  const filteredMembers = useMemo(() => {
    let result = members;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((m: any) => {
        const name = (m.user?.display_name || '').toLowerCase();
        const id = (m.user?.entity?.id || '').toLowerCase();
        return name.includes(q) || id.includes(q);
      });
    }
    if (statusFilter !== 'all') {
      result = result.filter((m: any) => m.membership?.status === statusFilter);
    }
    return result;
  }, [members, search, statusFilter]);

  const filteredInvitations = useMemo(() => {
    if (!search.trim()) return invitations;
    const q = search.toLowerCase();
    return invitations.filter((inv: any) =>
      (inv.invitee_email || '').toLowerCase().includes(q) ||
      (inv.inviter?.display_name || '').toLowerCase().includes(q)
    );
  }, [invitations, search]);

  // Stats
  const activeCount = members.filter((m: any) => m.membership?.status === 'MEMBERSHIP_STATUS_ACTIVE').length;
  const pendingCount = members.filter((m: any) => m.membership?.status === 'MEMBERSHIP_STATUS_PENDING').length;
  const pendingInvCount = invitations.filter((i: any) => i.status === 'INVITATION_STATUS_PENDING').length;

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !addProfileId.trim()) return;
    setAdding(true);
    try {
      await store.AddTeamMember(id, { member: { profile: { id: addProfileId.trim() } } } as any);
      setAddProfileId('');
      setShowAddMember(false);
      showSuccess(t('team.memberAdded'));
      loadMembers();
    } catch { /* store handles */ } finally { setAdding(false); }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!id) return;
    try {
      await store.RemoveTeamMember(id, memberId);
      setRemovingId(null);
      showSuccess(t('team.memberRemoved'));
      loadMembers();
    } catch { /* store handles */ }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !inviteProfileId) return;
    setInviting(true);
    try {
      await store.CreateTeamInvitation(id, {
        invitation: {
          invitee_profile: { id: inviteProfileId },
          message: inviteMessage.trim(),
        },
      } as any);
      setInviteProfileId('');
      setInviteMessage('');
      setShowInvite(false);
      showSuccess(t('team.invitationSent'));
      loadInvitations();
    } catch { /* store handles */ } finally { setInviting(false); }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!id) return;
    try {
      await store.CancelTeamInvitation(invitationId, { invitation: { id: invitationId }, comment: '' } as any);
      setCancellingId(null);
      showSuccess(t('team.invitationCancelled'));
      loadInvitations();
    } catch { /* store handles */ }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  const STATUS_FILTERS = [
    { value: 'all', labelKey: 'common.all' },
    { value: 'MEMBERSHIP_STATUS_ACTIVE', labelKey: 'team.memberStatus.active' },
    { value: 'MEMBERSHIP_STATUS_PENDING', labelKey: 'team.memberStatus.pending' },
    { value: 'MEMBERSHIP_STATUS_SUSPENDED', labelKey: 'team.memberStatus.suspended' },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground transition-colors">{t('team.list')}</Link>
        <svg className="h-4 w-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground transition-colors">{teamName}</Link>
        <svg className="h-4 w-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <span className="text-foreground font-medium">{t('team.members')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="members" />


      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <h1 className="sr-only">{t('team.members')}</h1>
        <div className="flex items-center gap-2 sm:ml-auto">
          {activeTab === 'members' && canRemove ? (
            <Button onClick={() => setShowAddMember(!showAddMember)}>
              {showAddMember ? t('common.cancel') : (
                <>
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>
                  {t('team.addMember')}
                </>
              )}
            </Button>
          ) : activeTab === 'invitations' && canInvite ? (
            <Button onClick={() => setShowInvite(!showInvite)}>
              {showInvite ? t('common.cancel') : (
                <>
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  {t('team.invite')}
                </>
              )}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-2xl font-bold text-foreground">{members.length}</div>
          <div className="text-xs text-muted-foreground">{t('team.stats.members')}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-2xl font-bold text-emerald-600">{activeCount}</div>
          <div className="text-xs text-muted-foreground">{t('team.memberStatus.active')}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
          <div className="text-xs text-muted-foreground">{t('team.memberStatus.pending')}</div>
        </div>
        {canInvite && <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-2xl font-bold text-primary">{pendingInvCount}</div>
          <div className="text-xs text-muted-foreground">{t('team.stats.invitations')}</div>
        </div>}
      </div>

      {/* Alerts */}
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      {/* Tabs + Search bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex gap-0 border-b border-border sm:border-b-0">
          {([
            { id: 'members' as MembersTab, label: t('team.members'), count: members.length },
            ...(canInvite ? [{ id: 'invitations' as MembersTab, label: t('team.invitations'), count: invitations.length }] : []),
          ]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 sm:rounded-lg sm:border-b-0 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary sm:bg-primary/10'
                  : 'border-transparent text-muted-foreground hover:text-foreground sm:hover:bg-muted'
              }`}
            >
              {tab.label}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 flex items-center gap-2 sm:ml-auto sm:max-w-md">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('team.searchPlaceholder')}
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            )}
          </div>
          {activeTab === 'members' && (
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} fullWidth={false}>
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{t(f.labelKey)}</option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {/* Add member form */}
      {canRemove && showAddMember && activeTab === 'members' && (
        <Card className="border-primary/30 shadow-sm">
          <CardHeader>
            <CardTitle>{t('team.addMember')}</CardTitle>
          </CardHeader>
          <form onSubmit={handleAddMember}>
            <CardContent>
              <Input
                placeholder={t('team.addMemberPlaceholder')}
                value={addProfileId}
                onChange={(e) => setAddProfileId(e.target.value)}
                required
              />
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowAddMember(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={adding || !addProfileId.trim()}>
                {adding ? t('common.loading') : t('team.addMember')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* Invite form */}
      {canInvite && showInvite && activeTab === 'invitations' && (
        <Card className="border-primary/30 shadow-sm">
          <CardHeader>
            <CardTitle>{t('team.invite')}</CardTitle>
            <CardDescription>{t('team.inviteDescription')}</CardDescription>
          </CardHeader>
          <form onSubmit={handleInvite}>
            <CardContent className="space-y-4">
              <UserPicker
                value={inviteProfileId}
                onChange={(userId) => setInviteProfileId(userId)}
                label={t('team.inviteUser')}
                placeholder={t('team.inviteUserPlaceholder')}
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">{t('team.inviteMessageLabel')}</label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder={t('team.inviteMessagePlaceholder')}
                  rows={2}
                  className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none transition-colors"
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowInvite(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={inviting || !inviteProfileId}>
                {inviting ? t('common.loading') : t('team.sendInvitation')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* Members tab content */}
      {activeTab === 'members' && (
        store.isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border p-4 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-28 rounded bg-muted" />
                    <div className="h-3 w-40 rounded bg-muted" />
                    <div className="flex gap-1">
                      <div className="h-5 w-16 rounded-full bg-muted" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredMembers.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground mb-1">
                {search ? t('team.noSearchResults') : t('team.noMembers')}
              </p>
              <p className="text-xs text-muted-foreground">
                {search ? t('team.tryDifferentSearch') : t('team.nav.membersDesc')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {search && (
              <p className="text-xs text-muted-foreground">
                {filteredMembers.length} / {members.length} {t('team.members').toLowerCase()}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredMembers.map((member: any) => {
                const memberId = getEntityId(member.membership) || member.user?.entity?.id || '';
                return (
                  <MemberCard
                    key={memberId || Math.random()}
                    member={member}
                    teamId={id!}
                    isRemoving={removingId === memberId}
                    onRemove={() => removingId === memberId ? handleRemoveMember(memberId) : setRemovingId(memberId)}
                    onCancelRemove={() => setRemovingId(null)}
                    t={t}
                    locale={locale}
                    canRemove={canRemove}
                  />
                );
              })}
            </div>
          </>
        )
      )}

      {/* Invitations tab content */}
      {canInvite && activeTab === 'invitations' && (
        store.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-48 rounded bg-muted" />
                    <div className="h-3 w-32 rounded bg-muted" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredInvitations.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{t('team.noInvitations')}</p>
              <Button size="sm" onClick={() => setShowInvite(true)}>
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                {t('team.invite')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {search && (
              <p className="text-xs text-muted-foreground">
                {filteredInvitations.length} / {invitations.length} {t('team.invitations').toLowerCase()}
              </p>
            )}
            {filteredInvitations.map((inv: any) => {
              const invId = getEntityId(inv);
              return (
                <InvitationCard
                  key={invId || Math.random()}
                  inv={inv}
                  isCancelling={cancellingId === invId}
                  onCancel={() => cancellingId === invId ? setCancellingId(null) : setCancellingId(invId)}
                  onCancelConfirm={() => handleCancelInvitation(invId)}
                  t={t}
                  locale={locale}
                />
              );
            })}
          </div>
        )
      )}
    </div>
  );
});

export default TeamMembersPage;
